import { useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Input, Spinner } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { usePostData, useFetchData } from "@/hooks";
import { showToast } from "@/utils";
import { ErrorState, ThemedText, PageHeader } from "@/components/ui";
import { LuArrowLeft, LuSave } from "react-icons/lu";

const RoleCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: { name: "" },
    });

    const fetchUrl = isEdit ? `roles/${id}/edit` : null;
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(fetchUrl);

    useEffect(() => {
        if (initialData) {
            reset({ name: initialData.name || "" });
        }
    }, [initialData, reset]);

    const endpoint = isEdit ? `roles/${id}/update` : "roles/store";
    const mutation = usePostData(endpoint);

    const onSubmit = (data) => {
        mutation.mutate(data, {
            method: isEdit ? "put" : "post",
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Role berhasil ${isEdit ? "diperbarui" : "ditambahkan"}.`,
                );
                navigate("/role");
            },
            onError: (err) => {
                if (err.response?.status === 422) {
                    showToast(
                        "error",
                        "Gagal",
                        err.response.data.errors?.name?.[0] ||
                            err.response.data.message,
                    );
                } else {
                    showToast("error", "Gagal", "Terjadi kesalahan pada sistem.");
                }
            },
        });
    };

    if (isEdit && fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data role. Silakan coba lagi."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-8">
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={isEdit ? "Edit Hak Akses" : "Tambah Hak Akses"}
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "HR" },
                        { label: "Hak Akses", href: "/role" },
                        { label: isEdit ? "Edit" : "Tambah" },
                    ]}
                />
            </div>

            <Card className="border-default-200 w-full border" shadow="none">
                <CardHeader className="bg-default-100/70 px-6 py-4">
                    <ThemedText className="font-semibold">
                        Informasi Hak Akses
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-6">
                    {isEdit && isFetching ? (
                        <div className="flex h-32 w-full items-center justify-center">
                            <Spinner label="Memuat data..." color="primary" />
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="flex flex-col gap-4"
                            noValidate
                        >
                            <Input
                                {...register("name", {
                                    required: "Nama role wajib diisi.",
                                    maxLength: {
                                        value: 100,
                                        message: "Nama role maksimal 100 karakter.",
                                    },
                                })}
                                label="Nama Hak Akses"
                                placeholder="Contoh: Admin, Kasir, Supervisor"
                                variant="bordered"
                                errorMessage={errors.name?.message}
                                isInvalid={!!errors.name}
                                isRequired
                            />

                            <div className="mt-4 flex justify-end gap-2">
                                <Button
                                    onPress={() => navigate("/role")}
                                    variant="light"
                                    color="danger"
                                    size="sm"
                                    isDisabled={mutation.isLoading}
                                >
                                    Batal
                                </Button>
                                <Button
                                    color="primary"
                                    type="submit"
                                    isLoading={mutation.isLoading}
                                    size="sm"
                                    startContent={
                                        !mutation.isLoading && <LuSave />
                                    }
                                >
                                    {isEdit ? "Simpan Perubahan" : "Simpan Hak Akses"}
                                </Button>
                            </div>
                        </form>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default RoleCreate;
